import React, {Fragment, useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import moment from "moment";
import _ from "lodash";
import BedasCalendarElement from "./calendarComponent/bedasCalendarElement";
import {useDispatch, useSelector} from 'react-redux';
import {getBedasList, getSingleBedasSessions, getSingleSessionsByDay} from '../../store/reducers/ward';
import {
    calculateSessionHeight,
    calculateSessionTop,
    calculateSessionWidth,
    getActiveSessions,
    getActiveSessionsByDay,
} from './calendarComponent/bedasSessionsManager';
import config from '../../app/config';
import {PadlockCloseIcon} from '../icons/icons';

const useStyles = makeStyles((theme) => ({
    timeline: {
        display: "flex",
        flexDirection: "column",
        paddingLeft: "10px",
        position: "relative",
    },
    timeSlot: {
        display: "flex",
        alignItems: "center",
        backgroundImage: `linear-gradient(to bottom, ${theme.palette.background.paperHover} 1px, transparent 1px)`,
        "&.odd": {
            backgroundImage: `linear-gradient(to bottom, ${theme.palette.background.paperDarker} 1px, transparent 1px)`,
        },
        zIndex: 10
    },
    time: {
        width: "60px",
        fontWeight: "bold",
        backgroundImage: `linear-gradient(to left, ${theme.palette.background.paperHover} 1px, transparent 1px)`,
        textAlign: "center",
        verticalAlign: "middle",
        "&.odd": {
            color: "grey",
        },
    },
    event: {
        flex: "1",
        backgroundColor: "lightblue",
        position: "absolute",
    },
    currentTime: {
        height: 2,
        backgroundColor: "darkred",
        position: "absolute",
    },
}));


export const SLOT_HEIGHT = 90;
export const DEFAULT_LEFT = 70;
export const LEFT_OFFSET = 2;
export const OFFSET = 2;
const START_OF_DAY = new Date().setHours(0, 0, 0, 0);
const END_OF_DAY = new Date().setHours(23, 59, 59, 999);

const AgyleCalendarDaily = ({dischargeForm, selectedDay, events, type, handleSelect, selectedMode, selectedTimes, selectTime, changeLockedReason, deleteTemporaryLockedSession, hospital}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const bedasSessions = useSelector(getBedasList(hospital?.hospitalId));
    const singleBedasSessions = useSelector(getSingleBedasSessions(hospital?.hospitalId));
    const [singleSessionsArr, setSingleSessionsArr] = useState([]);
    const oldCal = config?.modules?.bedas?.bedasOldCalendar;

    // Events
    const [filteredSessions, setFilteredSessions] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [groupedEvents, setGroupedEvents] = useState({eventGroups: [], eventSingles: []});
    const refTimeline = useRef(null);
    const timelineWidth = refTimeline.current?.clientWidth - DEFAULT_LEFT;

    // Current timeline
    const [currentTime, setCurrentTime] = useState(moment());
    const currenLine = useRef(null);

    // Check if there are booked sessions for selected day and filter them
    useEffect(() => {
        if (!_.isEmpty(bedasSessions)) {
            const validActiveSessions = getActiveSessions(bedasSessions, selectedDay);
            const valid = getActiveSessionsByDay(validActiveSessions, selectedDay);

            setFilteredSessions(valid);
        }
    }, [bedasSessions, selectedDay])

    // Update current time line
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(moment());
        }, 60000);

        if (currenLine.current) currenLine.current.scrollIntoView({behavior: "smooth", block: "center"});

        return () => {
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        const filtered = _.filter(events, e => {
            return moment(getStartDate(e)).isSame(moment(selectedDay), "day");
        });
        setFilteredEvents(filtered);
    }, [events, selectedDay]);

    useEffect(() => {
        setGroupedEvents(groupEventsByOverlap(filteredEvents));
    }, [filteredEvents]);

    useEffect(() => {
        if (selectedDay) {
            const days = dispatch(getSingleSessionsByDay(selectedDay, hospital?.hospitalId));
            setSingleSessionsArr(days);
        }
    }, [selectedDay, hospital?.hospitalId, dispatch]);

    // Creat timeline
    const timeSlots = [];
    let currentDay = START_OF_DAY;
    while (currentDay <= END_OF_DAY) {
        timeSlots.push(new Date(currentDay));
        currentDay += 30 * 60 * 1000; // increment by 30 minutes
    }

    const calculateDuration = (start, end) => {
        return moment.duration(moment(end).diff(moment(start))).asMinutes();
    };

    // Calculate top style for each event
    const calculateTopForCurrentTime = () => {
        const startOfDay = moment(currentTime).startOf("day");
        const minutesFromStartOfDay = currentTime.diff(startOfDay, "minutes");
        const top = (minutesFromStartOfDay / 30) * SLOT_HEIGHT;
        return `${top}px`;
    };

    // Calculate time for position of top
    const calculateTimeForTop = (top, temporaryLock) => {
        const minutesFromStartOfDay = top / SLOT_HEIGHT * 30;
        const startOfDay = moment(selectedDay).startOf("day");
        return startOfDay.add(minutesFromStartOfDay, "minutes");
    };

    const getStartDate = (event) => {
        switch (type) {
            case "bedas":
                return event?.bedas?.date;
            case "bedasList":
                return event?.startDate;
            default:
                return event?.start;
        }
    };

    const getEndDate = (event, string) => {
        switch (type) {
            case "bedas":
                const momentObj = moment(event?.bedas?.date).add(30, "minutes");
                return string ? momentObj.toISOString() : momentObj;
            default:
                return event?.end;
        }
    };

    const getId = () => {
        switch (type) {
            case "bedas":
                return "__id";
            default:
                return "id";
        }
    };

    // Calculate the top style for each event
    const calculateTop = (event) => {
        const startDate = getStartDate(event);
        const startOfDay = moment(startDate).startOf("day");
        const minutesFromStartOfDay = moment(startDate).diff(startOfDay, "minutes");
        const top = ((minutesFromStartOfDay / 30) * SLOT_HEIGHT) + OFFSET;
        return `${top}px`;
    };

    const groupEventsByOverlap = (freshEvents) => {
        let eventGroups = [];
        let eventSingles = [];

        if (!_.isEmpty(freshEvents)) {
            // sort events by start time
            const sortedEvents = freshEvents.slice().sort((a, b) => moment(getStartDate(a)) - moment(getStartDate(b)));

            // iterate through sorted events and group them by overlap
            for (let i = 0; i < sortedEvents.length; i++) {
                let event = sortedEvents[i];
                let group = [event];

                for (let j = i + 1; j < sortedEvents.length; j++) {
                    let otherEvent = sortedEvents[j];

                    if (moment(getStartDate(otherEvent)).format(config.defaultDateTimeFormat) < moment(getEndDate(event, true)).format(config.defaultDateTimeFormat)) {
                        group.push(otherEvent);
                        event = otherEvent;
                    } else {
                        break;
                    }
                }
                if (group.length > 1) eventGroups.push(group);
                else eventSingles.push(group[0]);
                i += group.length - 1;
            }
        }
        return {eventGroups, eventSingles};
    };

    // We calculate the left style for each event
    const calculateLeft = (event, isBedasEvent) => {
        const ID = getId();
        let left = DEFAULT_LEFT;

        if (!_.isEmpty(groupedEvents?.eventGroups) && !_.find(groupedEvents?.eventSingles, {[ID]: event[ID]})) {
            let done = false;
            _.forEach(groupedEvents?.eventGroups, grEv => {
                if (done) return;

                const index = _.findIndex(grEv, {[ID]: event[ID]});
                if (index !== -1) {
                    done = true;
                    left += index * ((timelineWidth / grEv.length) + LEFT_OFFSET);
                }
            });
        }
        return `${left}px`;
    };

    // Calculate height style for each event
    const calculateHeight = (event) => {
        const startDate = getStartDate(event);
        const endDate = getEndDate(event);

        const height = (calculateDuration(startDate, endDate) / 30 * SLOT_HEIGHT) - OFFSET * 2;
        return `${height}px`;
    };

    // Calculate width style for each event
    const calculateWidth = (event, isBedasEvent) => {
        const ID = getId();
        let width = timelineWidth;

        if (isBedasEvent) width += 10;

        if (!_.isEmpty(groupedEvents) && !_.find(groupedEvents?.eventSingles, {[ID]: event[ID]})) {
            let done = false;
            _.forEach(groupedEvents?.eventGroups, grEv => {
                if (done) return;

                const index = _.findIndex(grEv, {[ID]: event[ID]});
                if (index !== -1) width = timelineWidth / grEv.length;
            });
        }
        return `${width}px`;
    };

    const handleSelectTime = (e) => {
        const timeline = document.getElementById("timeline-calendar");
        const rect = timeline.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset;
        const clientY = e.clientY + scrollY;
        const posY = clientY - rect.top - scrollY;

        if (selectedMode) {
            selectTime(handleSelectDateType(calculateTimeForTop(posY, true)));
            return;
        }
        handleSelectDateType(calculateTimeForTop(posY));
    };

    const handleSelectDateType = (time, temporaryLock) => {
        switch (type) {
            case "bedas":
                let selectedTime = time;
                if (selectedTime.minutes() >= 30) selectedTime.minutes(30);
                else selectedTime.minutes(0);
                selectedTime.seconds(0);
                selectedTime.milliseconds(0);
                if (temporaryLock) {
                    const time = moment(selectedTime);
                    handleSelect(time);
                    return;
                }
                handleSelect(selectedTime);
                break;
            default:
                console.info("Not implemented");
                break;
        }
    };

    const renderSession = () => {
        if (!_.isEmpty(filteredSessions)) {
            return filteredSessions.map(session => {
                return (
                    <div
                        key={session?.sessionId || Math.random()}
                        style={{zIndex: 11}}
                        onClick={!session?.locked ? handleSelectTime : null}
                    >
                      <BedasCalendarElement
                        event={session}
                        calendarType="daily"
                        session
                        locked={session?.locked}
                        onSelectTime={handleSelectTime}
                        options={{
                            top: calculateSessionTop(session, selectedDay,SLOT_HEIGHT, OFFSET),
                            left: calculateLeft(session),
                            height: calculateSessionHeight(session, selectedDay, SLOT_HEIGHT, OFFSET),
                            width: calculateSessionWidth(DEFAULT_LEFT-10),
                        }}
                      />
                    </div>
                )}
            );
        }
        return null;
    }

    const renderTemporaryLockedSession = () => {
        if (!_.isEmpty(selectedTimes)) {
            return selectedTimes.map(time => (
                <div
                    style={{zIndex: 12}}
                    key={time?.from || Math.random()}
                >
                    <BedasCalendarElement
                        event={time}
                        calendarType="daily"
                        session
                        temporaryLock
                        changeLockedReason={changeLockedReason}
                        deleteTemporaryLockedSession={deleteTemporaryLockedSession}
                        className={classes.event}
                        options={{
                            top: calculateSessionTop(time, selectedDay,SLOT_HEIGHT, OFFSET, true),
                            left: calculateLeft(time),
                            height: calculateSessionHeight(time, selectedDay, SLOT_HEIGHT, OFFSET, true),
                            width: calculateSessionWidth(DEFAULT_LEFT-10),
                        }}
                    />
                </div>
            ));
        }
    }

    const renderSingleSession = () => {
        if (!_.isEmpty(singleSessionsArr)) {
            return singleSessionsArr.map(time => (
                <div
                    style={{zIndex: 11}} onClick={!time?.locked ? handleSelectTime : null}
                    key={time?.id || Math.random()}
                >
                    <BedasCalendarElement
                        event={time}
                        calendarType="daily"
                        session
                        singleSession
                        locked={time?.locked}
                        onSelectTime={handleSelectTime}
                        changeLockedReason={changeLockedReason}
                        deleteTemporaryLockedSession={deleteTemporaryLockedSession}
                        className={classes.event}
                        options={{
                            top: calculateSessionTop(time, selectedDay,SLOT_HEIGHT, OFFSET, true),
                            left: calculateLeft(time),
                            height: calculateSessionHeight(time, selectedDay, SLOT_HEIGHT, OFFSET, true),
                            width: calculateSessionWidth(DEFAULT_LEFT-10),
                        }}
                    />
                </div>
            ));
        }
    }

    const handleSelectTimeDischargeForm = (e) => {
        if (dischargeForm && config?.modules?.bedas?.allowMultiplePatientsOnOneHour) {
            handleSelectTime(e);
        }
    }

    // Render events in a timeline
    const renderEvents = () => {
        if (!_.isEmpty(filteredEvents)) {
            switch (type) {
                case "bedas":
                    return filteredEvents.map(event => (
                        // <div style={{zIndex: 20}}>
                            <BedasCalendarElement
                                key={event?.__id}
                                event={event}
                                calendarType="daily"
                                onSelectTime={handleSelectTimeDischargeForm}
                                options={{
                                    top: calculateTop(event),
                                    left: calculateLeft(event, true),
                                    height: calculateHeight(event),
                                    width: calculateWidth(event, true),
                                }}
                            />
                        // </div>
                    ));
                default:
                    return filteredEvents.map(event => (
                        <div
                            key={event.id}
                            className={classes.event}
                            style={{
                                top: calculateTop(event),
                                left: calculateLeft(event),
                                height: calculateHeight(event),
                                width: calculateWidth(event),
                            }}
                        >
                            {event.title}
                        </div>
                    ));
            }
        }
        return null;
    };

    const renderCurrentTime = () => {
        return (
            <div
                ref={currenLine}
                className={classes.currentTime}
                style={{
                    top: calculateTopForCurrentTime(),
                    left: DEFAULT_LEFT,
                    width: `calc(100% - ${DEFAULT_LEFT}px)`,
                    zIndex: 111
                }}
            />
        );
    };

    return (
        <>
            <h2 style={{textAlign: "center"}}>
                {moment(selectedDay).format("dddd | DD MM yyyy")}
            </h2>

            {/*<div id="timeline-calendar" onClick={handleSelectTime}>*/}
            <div id="timeline-calendar">
                <div
                    className={classes.timeline}
                    ref={refTimeline}
                    onClick={(e) => {
                        if (oldCal || selectedMode) handleSelectTime(e);
                    }}
                >
                    {timeSlots.map((timeSlot, index) => {
                        const isEven = index % 2 === 0;
                        const time = timeSlot.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"});
                        return (
                            <div
                                key={time}
                                // className={clsx(classes.timeSlot, !isEven && "odd")}
                                className={clsx(classes.timeSlot)}
                            >
                                <span
                                    className={clsx(classes.time, !isEven && "odd")}
                                    style={{height: SLOT_HEIGHT}}
                                >
                                    {time}
                                </span>
                            </div>
                        );
                    })}

                    {renderEvents()}
                    {renderCurrentTime()}
                    {!oldCal && renderSession()}
                    {!oldCal && !_.isEmpty(selectedTimes) && renderTemporaryLockedSession()}
                    {!oldCal && !_.isEmpty(singleSessionsArr) && renderSingleSession()}
                </div>
            </div>
        </>
    );
};

export default AgyleCalendarDaily;